import type {
  ReviewFormData,
  ReviewOption,
  ReviewableProduct,
} from '@iheartjane/menu';
import {
  INITIAL_REVIEW_FORM_DATA,
  RateAndReviewFormModal,
  RateAndReviewWizardModal,
} from '@iheartjane/menu';
import { useEffect, useState } from 'react';

import { useFetcherWithMutation } from '@jane/bloom/hooks/useFetcherWithMutation';
import { useLeaveReviewAbTest } from '@jane/bloom/hooks/useLeaveReviewAbTest';
import type { LeaveReviewSource } from '@jane/bloom/services/tracking/Events';
import {
  trackLoadedWriteProductReviewForm,
  trackReviewedProduct,
} from '@jane/bloom/services/tracking/track';

import type { LeaveReviewProduct } from './types';

type ReviewSubmission = {
  activities: number[];
  comment: string;
  feelings: number[];
  productId: number;
  rating: number;
};

export interface LeaveReviewModalProps {
  activityOptions: ReviewOption[];
  feelingOptions: ReviewOption[];
  isMobile: boolean;
  moreToReview: LeaveReviewProduct[];
  onClose: () => void;
  product: LeaveReviewProduct;
  source: LeaveReviewSource;
}

const labelsFor = (ids: number[], options: ReviewOption[]) =>
  options.filter(({ id }) => ids.includes(id)).map(({ label }) => label);

export const LeaveReviewModal = ({
  activityOptions,
  feelingOptions,
  isMobile,
  moreToReview,
  onClose,
  product: initialProduct,
  source,
}: LeaveReviewModalProps) => {
  const showWizard = useLeaveReviewAbTest(true);
  const [product, setProduct] = useState(initialProduct);
  const [queue, setQueue] = useState(() =>
    moreToReview.filter(({ id }) => id !== initialProduct.id)
  );
  const [value, setValue] = useState<ReviewFormData>(INITIAL_REVIEW_FORM_DATA);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    trackLoadedWriteProductReviewForm(source);
  }, [product.id, source]);

  const { isPending, mutate } = useFetcherWithMutation<ReviewSubmission, null>({
    action: '/api/product-reviews',
  });

  const handleSubmit = () => {
    const submitted = { productId: product.id, ...value };
    mutate(submitted, {
      onError: (errors) => {
        // TODO: show the error in the modal once design decides how it should look.
        // eslint-disable-next-line no-restricted-syntax
        console.error('Failed to submit product review', errors);
      },
      onSuccess: () => {
        setIsSubmitted(true);
        trackReviewedProduct({
          activities: labelsFor(submitted.activities, activityOptions),
          feelings: labelsFor(submitted.feelings, feelingOptions),
          hasComment: submitted.comment.trim() !== '',
          productId: product.id,
          productName: product.name,
          rating: submitted.rating,
          source,
        });
      },
    });
  };

  const handleReviewProduct = ({ id }: ReviewableProduct) => {
    const next = queue.find((queued) => queued.id === id);
    if (!next) return;

    setProduct(next);
    setQueue(queue.filter((queued) => queued.id !== id));
    setValue(INITIAL_REVIEW_FORM_DATA);
    setIsSubmitted(false);
  };

  const Variant = showWizard
    ? RateAndReviewWizardModal
    : RateAndReviewFormModal;

  return (
    <Variant
      activityOptions={activityOptions}
      brand={product.brand}
      category={product.category}
      feelingOptions={feelingOptions}
      imageUrl={product.imageUrl}
      isMobile={isMobile}
      isSubmitted={isSubmitted}
      isSubmitting={isPending}
      moreToReview={queue}
      onChange={setValue}
      onRequestClose={onClose}
      onReviewProduct={handleReviewProduct}
      onSubmit={handleSubmit}
      open
      productName={product.name}
      value={value}
    />
  );
};
